import {
  isRetryable,
  StoryFailure,
  type Receipt,
  type StoryAction,
} from '../domain/story'
import type {
  PendingCommand,
  PendingStorage,
  StoryClock,
  StoryTransport,
} from './ports'

const delays = [1000, 3000, 10_000] as const

export class PendingDelivery {
  private account: string | null = null
  private entries: PendingCommand[]
  private epoch = 0
  private busy = new Set<string>()
  private attempts = new Map<string, number>()
  private timers = new Map<string, () => void>()

  constructor(
    private readonly storage: PendingStorage,
    private readonly transport: StoryTransport,
    private readonly clock: StoryClock,
    private readonly accepted: (receipt: Receipt) => void,
    private readonly rejected: (reservation: string, error: unknown) => void,
  ) {
    this.entries = storage.read()
  }

  setAccount(account: string | null) {
    if (this.account === account) return
    this.stop()
    this.account = account
    this.entries = this.entries.filter(
      (entry) => entry.account === account && entry.deadline > this.clock.now(),
    )
    this.persist()
  }

  enqueue(command: PendingCommand) {
    if (command.account !== this.account) return
    this.entries = this.entries.filter(
      (entry) => entry.deadline > this.clock.now(),
    )
    const existing = this.entries.find(
      (entry) => entry.reservationId === command.reservationId,
    )
    if (existing) {
      existing.action ??= command.action
      existing.impressionAccepted ||= command.impressionAccepted
    } else this.entries.push(command)
    this.entries = this.entries.slice(-32)
    this.persist()
    void this.send(command.reservationId)
  }

  wake() {
    this.attempts.clear()
    for (const entry of this.entries) void this.send(entry.reservationId)
  }

  stop() {
    this.epoch += 1
    this.timers.forEach((cancel) => cancel())
    this.timers.clear()
    this.busy.clear()
    this.attempts.clear()
  }

  private persist() {
    this.storage.write(this.entries)
  }

  private async send(id: string) {
    if (this.busy.has(id) || this.timers.has(id)) return
    const entry = this.entries.find((item) => item.reservationId === id)
    if (!entry || entry.account !== this.account) return
    if (entry.deadline <= this.clock.now()) {
      this.remove(id)
      return
    }
    if (entry.impressionAccepted && !entry.action) {
      this.remove(id)
      return
    }
    const epoch = this.epoch
    const action: StoryAction | null = entry.action
    this.busy.add(id)
    try {
      const command = { clientId: entry.clientId, reservationId: id }
      const reply = await (action
        ? this.transport.interact({ ...command, action })
        : this.transport.impression(command))
      if (epoch !== this.epoch) return
      this.clock.observeServerTime?.(reply.serverTime)
      if (reply.value.reservationId !== id)
        throw new StoryFailure('contract', 'STORY_RECEIPT_MISMATCH')
      this.accepted(reply.value)
      entry.impressionAccepted = true
      if (action || !entry.action) this.remove(id)
      else this.persist()
      this.busy.delete(id)
      if (!action && entry.action) void this.send(id)
    } catch (error) {
      if (epoch !== this.epoch) return
      this.busy.delete(id)
      if (!isRetryable(error)) {
        this.remove(id)
        this.rejected(id, error)
        return
      }
      const attempt = this.attempts.get(id) ?? 0
      const delay = delays[attempt]
      if (delay === undefined) return
      this.attempts.set(id, attempt + 1)
      const wait = Math.max(
        delay,
        error instanceof StoryFailure ? error.retryAfterMs : 0,
      )
      if (this.clock.now() + wait >= entry.deadline) {
        this.remove(id)
        return
      }
      this.timers.set(
        id,
        this.clock.schedule(() => {
          this.timers.delete(id)
          void this.send(id)
        }, wait),
      )
    }
  }

  private remove(id: string) {
    this.entries = this.entries.filter((entry) => entry.reservationId !== id)
    this.attempts.delete(id)
    this.persist()
  }
}
